import { useState, useEffect, useCallback, useRef } from 'react';
import { popupService, type PopupData } from '../services/popupService';
import { queueApiRequest, REQUEST_PRIORITY } from '../services/ApiRequestManager';

const POPUP_SYSTEM_STATUS_TIMEOUT_MS = 3_500;

const fetchWithTimeout = async (
    input: RequestInfo | URL,
    init: RequestInit = {},
    timeoutMs: number = POPUP_SYSTEM_STATUS_TIMEOUT_MS
): Promise<Response> => {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);

    try {
        return await fetch(input, { ...init, signal: controller.signal });
    } finally {
        window.clearTimeout(timeoutId);
    }
};

interface UsePopupManagerOptimizedProps {
    tenantId: string | null;
    route?: string;
    enabled?: boolean;
}

interface UsePopupManagerOptimizedReturn {
    currentPopup: PopupData | null;
    popupQueue: PopupData[];
    isLoading: boolean;
    error: string | null;
    dismissPopup: (popupId: string, dontShowAgain?: boolean) => Promise<void>;
    forceShowPopup: (popup: PopupData) => void;
    clearAllPopups: () => void;
    refreshPopups: () => Promise<void>;
}

export const usePopupManagerOptimized = ({
    tenantId,
    route = '/',
    enabled = true
}: UsePopupManagerOptimizedProps): UsePopupManagerOptimizedReturn => {
    const [currentPopup, setCurrentPopup] = useState<PopupData | null>(null);
    const [popupQueue, setPopupQueue] = useState<PopupData[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Rate limiting protection for frontend-classic
    const lastApiCall = useRef<number>(0);
    const minApiInterval = 2000; // 2 seconds between API calls (conservative for user frontend)
    const loadingPopupsRef = useRef<boolean>(false);

    // Initialization protection
    const hasInitialized = useRef<boolean>(false);
    const initTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Rate limiting protection (from original usePopupManager)
    const isFetchingStatusRef = useRef(false);
    const lastStatusCheckTime = useRef<number>(0);
    const statusCheckCooldown = 30000; // 30 seconds between status checks

    // Popup system status state - start with null to indicate "not checked yet"
    const [popupSystemEnabled, setPopupSystemEnabled] = useState<boolean | null>(null);

    const getEligiblePopupRoute = useCallback(() => {
        const currentPath = window.location.pathname || '/';
        const normalizedPath = currentPath.split('?')[0].split('#')[0] || '/';
        if (normalizedPath === '/' || normalizedPath === '/dashboard') {
            return '/';
        }
        if (route !== '/') {
            return route;
        }
        return null;
    }, [route]);

    const isPopupEligibleRoute = useCallback(() => {
        return getEligiblePopupRoute() !== null;
    }, [getEligiblePopupRoute]);

    // 🚨 Early return if not on homepage OR system disabled OR status not checked yet
    const shouldShowPopups = enabled && tenantId && isPopupEligibleRoute() && popupSystemEnabled === true;



    // Show next popup from queue
    const showNextPopup = useCallback(() => {
        if (currentPopup || popupQueue.length === 0 || !shouldShowPopups) return;

        const nextPopup = popupQueue[0];
        setCurrentPopup(nextPopup);
        setPopupQueue(prev => prev.slice(1));


    }, [currentPopup, popupQueue, shouldShowPopups]);

    // 🎯 WebSocket-based popup system status check (no API polling)
    const handlePopupSystemToggle = useCallback((enabled: boolean) => {


        // Update system status
        setPopupSystemEnabled(enabled);

        // Store to localStorage for persistence (same key as admin panel)
        try {
            const localStorageKey = 'popup-system-toggle';
            const statusData = {
                type: 'system-toggle',
                popupSystemEnabled: enabled,
                tenantId: tenantId,
                timestamp: Date.now(),
                source: 'websocket-sync'
            };
            localStorage.setItem(localStorageKey, JSON.stringify(statusData));


        } catch (error) {
            console.error('❌ Failed to save popup system status to localStorage:', error);
        }

        if (!enabled) {
            // Clear current popup and queue when system disabled
            setCurrentPopup(null);
            setPopupQueue([]);
            console.log('❌ Popup system disabled - clearing all popups globally');
        } else {
            console.log('✅ Popup system enabled - system ready for popups');
            // Note: We don't auto-refresh popups here as it should only show on homepage
        }
    }, [tenantId]);

    // 🔄 Initial popup fetch with rate limiting protection
    const fetchInitialPopups = useCallback(async (forceEnabled?: boolean) => {
        // Check if system is enabled (either forced or from current state)
        const systemEnabled = forceEnabled !== undefined ? forceEnabled : popupSystemEnabled === true;
        const popupRoute = getEligiblePopupRoute();

        if (!enabled || !tenantId || !systemEnabled || !popupRoute) {

            return;
        }

        // Rate limiting check
        const now = Date.now();
        if (now - lastApiCall.current < minApiInterval) {

            return;
        }

        // Singleton protection
        if (loadingPopupsRef.current) {

            return;
        }

        try {
            loadingPopupsRef.current = true;
            setIsLoading(true);
            setError(null);
            lastApiCall.current = now;



            // Use ApiRequestManager for coordinated API calls
            const popups = await queueApiRequest(
                () => popupService.getActivePopups(tenantId!, popupRoute),
                REQUEST_PRIORITY.NORMAL,
                `Get Active Popups (${tenantId})`
            );

            if (popups.length > 0) {
                // Filter out dismissed popups
                const filteredPopups = popups.filter(popup => {
                    const isDismissed = popupService.isPopupDismissed(popup._id, tenantId!);
                    if (isDismissed) {
                        console.log(`🚫 Filtering out dismissed popup: ${popup.title}`);
                    }
                    return !isDismissed;
                });

                // Sort by priority and creation date
                const sortedPopups = filteredPopups.sort((a, b) => {
                    if (a.priority !== b.priority) {
                        return b.priority - a.priority;
                    }
                    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
                });

                setPopupQueue(sortedPopups);


            } else {

            }

        } catch (err) {
            // Handle rate limiting gracefully
            if (err instanceof Error && err.message.includes('429')) {
                console.warn('⚠️ Rate limited - popup fetch will retry later');
                setError('กำลังโหลดข้อมูล popup... โปรดรอสักครู่');

                // Retry after delay
                setTimeout(() => {
                    if (Date.now() - lastApiCall.current > 3000) {
                        fetchInitialPopups(); // Use current state for retry
                    }
                }, 3000);
            } else {
                console.error('Failed to fetch initial popups:', err);
                setError(err instanceof Error ? err.message : 'Failed to fetch popups');
            }
        } finally {
            setIsLoading(false);
            loadingPopupsRef.current = false;
        }
    }, [enabled, getEligiblePopupRoute, popupSystemEnabled, tenantId]);

    // 🏠 Route change detection - Clear popups when leaving supported routes
    useEffect(() => {
        const handleRouteChange = () => {
            if (!isPopupEligibleRoute()) {
                // Clear current popup and queue when leaving supported routes
                setCurrentPopup(null);
                setPopupQueue([]);
                console.log('🚪 Left popup-eligible route - cleared popups');
            }
        };

        // Listen for route changes
        window.addEventListener('popstate', handleRouteChange);

        // Also check on initial load
        handleRouteChange();

        return () => {
            window.removeEventListener('popstate', handleRouteChange);
        };
    }, [isPopupEligibleRoute]);

    // 🔍 Check popup system status with API fallback (pattern from original usePopupManager)
    const checkPopupSystemStatus = useCallback(async () => {
        if (!tenantId) {
            console.warn('❌ No tenantId provided for popup system status check');
            return false;
        }

        if (!isPopupEligibleRoute()) {
            setPopupSystemEnabled(false);
            return false;
        }

        // Rate limiting protection
        const now = Date.now();
        if (isFetchingStatusRef.current || (now - lastStatusCheckTime.current < statusCheckCooldown)) {

            return popupSystemEnabled !== false; // Return current status or default to true
        }

        try {
            isFetchingStatusRef.current = true;
            lastStatusCheckTime.current = now;

            // 1. Check localStorage first (same-port admin panel)
            const localStorageKey = 'popup-system-toggle';
            const storedStatus = localStorage.getItem(localStorageKey);



            if (storedStatus) {
                const statusData = JSON.parse(storedStatus);
                if (statusData.type === 'system-toggle' && statusData.tenantId === tenantId) {
                    const systemEnabled = statusData.popupSystemEnabled;



                    setPopupSystemEnabled(systemEnabled);

                    if (!systemEnabled) {
                        setCurrentPopup(null);
                        setPopupQueue([]);
                        console.log('❌ Popup system disabled via localStorage - no popups will be shown');
                        return false;
                    } else {
                        console.log('✅ Popup system enabled via localStorage');
                        return true;
                    }
                }
            }

            // 2. API fallback (cross-port admin panel or persistent status)


            // Add delay to prevent rapid API calls (pattern from original)
            await new Promise(resolve => setTimeout(resolve, 1000));

            const apiResponse = await queueApiRequest(
                async () => {
                    const response = await fetchWithTimeout(
                        `${popupService.getBaseUrl()}/api/v1/configuration-service/popups/${tenantId}/system-status`
                    );

                    if (!response.ok) {
                        if (response.status === 429) {
                            console.warn('📊 Popup system: Rate limited, defaulting to enabled');
                            throw new Error('Rate limited');
                        }
                        throw new Error(`API Error: ${response.status}`);
                    }

                    return await response.json();
                },
                REQUEST_PRIORITY.HIGH,
                `Check Popup System Status (${tenantId})`
            );

            if (apiResponse.success) {
                const systemEnabled = apiResponse.data.popupSystemEnabled;

                setPopupSystemEnabled(systemEnabled);

                if (!systemEnabled) {
                    setCurrentPopup(null);
                    setPopupQueue([]);
                    console.log('❌ Popup system disabled via API - clearing existing popups immediately');

                    // Force immediate UI update by triggering handlePopupSystemToggle
                    handlePopupSystemToggle(false);
                    return false;
                } else {
                    console.log('✅ Popup system enabled via API');
                    return true;
                }
            }

            // 3. Default fallback: assume enabled

            setPopupSystemEnabled(true);
            return true;

        } catch (error) {

            setPopupSystemEnabled(true);
            return true;

        } finally {
            isFetchingStatusRef.current = false;
        }
    }, [isPopupEligibleRoute, popupSystemEnabled, tenantId]);

    // 📡 WebSocket event handlers for real-time popup management
    useEffect(() => {
        if (!enabled || !tenantId) return;

        // Listen for WebSocket popup events from useSmartWebSocket
        const handlePopupEvent = (event: CustomEvent) => {
            const { type, data } = event.detail;



            switch (type) {
                case 'popup_show':
                    // Only process popup_show if system is enabled
                    if (shouldShowPopups && data.popup) {
                        forceShowPopup(data.popup);
                    }
                    break;

                case 'popup_system_toggle':
                case 'admin_popup_system_toggle':
                    // Always process system toggle events regardless of current state
                    const enabled = data.enabled || data.data?.enabled; // Handle both direct and nested data
                    console.log(`🎯 Processing ${type}: ${enabled ? 'ENABLED' : 'DISABLED'}`);

                    // Sync localStorage immediately when receiving WebSocket events
                    try {
                        const localStorageKey = 'popup-system-toggle';
                        const statusData = {
                            type: 'system-toggle',
                            popupSystemEnabled: enabled,
                            tenantId: tenantId,
                            timestamp: Date.now(),
                            source: 'websocket-sync'
                        };
                        localStorage.setItem(localStorageKey, JSON.stringify(statusData));
                    } catch (error) {
                        console.warn('Failed to sync localStorage:', error);
                    }

                    handlePopupSystemToggle(enabled);
                    break;

                case 'popup_clear_sessions':
                    clearAllPopups();
                    // Clear popup dismissals using popupService
                    if (tenantId) {
                        popupService.clearPopupDismissals(tenantId);
                    }
                    break;

                case 'popup_refresh':
                    // Refresh popups without API polling
                    fetchInitialPopups();
                    break;

                default:
                    break;
            }
        };

        // Listen for custom popup events
        window.addEventListener('popup_websocket_event', handlePopupEvent as EventListener);

        return () => {
            window.removeEventListener('popup_websocket_event', handlePopupEvent as EventListener);

        };
    }, [enabled, tenantId, handlePopupSystemToggle, fetchInitialPopups]);

    // 📻 BroadcastChannel fallback for cross-tab communication (when WebSocket fails)
    useEffect(() => {
        if (!enabled || !tenantId) return;

        let channel: BroadcastChannel | null = null;
        let messageHandler: ((event: MessageEvent) => void) | null = null;

        try {
            channel = new BroadcastChannel('popup-system-events');



            messageHandler = (event: MessageEvent) => {


                if (event.data && event.data.type === 'system-toggle') {
                    console.log('🎯 Processing system-toggle via BroadcastChannel...');

                    const newSystemStatus = event.data.popupSystemEnabled;
                    handlePopupSystemToggle(newSystemStatus);

                    console.log(`🔄 Popup system ${newSystemStatus ? 'ENABLED' : 'DISABLED'} via BroadcastChannel`);

                    // Store status to localStorage for persistence (same key as admin panel)
                    const localStorageKey = 'popup-system-toggle';
                    const statusData = {
                        type: 'system-toggle',
                        popupSystemEnabled: newSystemStatus,
                        tenantId: tenantId,
                        timestamp: Date.now(),
                        source: 'broadcast-channel'
                    };
                    localStorage.setItem(localStorageKey, JSON.stringify(statusData));
                    console.log('💾 Stored popup system status to localStorage for persistence');
                } else {

                }
            };

            channel.addEventListener('message', messageHandler);



        } catch (error) {
            console.error('❌ Failed to create BroadcastChannel for popup optimization:', error);
        }

        return () => {
            if (channel && messageHandler) {
                console.log('🧹 Cleaning up popup BroadcastChannel...');
                channel.removeEventListener('message', messageHandler);
                channel.close();
            }
        };
    }, [enabled, tenantId, handlePopupSystemToggle]);

    // Dismiss popup
    const dismissPopup = useCallback(async (popupId: string, dontShowAgain: boolean = false) => {
        if (!tenantId) return;

        try {
            const remainingQueue = popupQueue.filter(p => p._id !== popupId);

            // Store dismissal in localStorage immediately
            if (dontShowAgain) {
                popupService.storeDismissedPopup(popupId, tenantId);
            }

            if (dontShowAgain) {
                const success = await popupService.dismissPopup(popupId, tenantId, 'manual');

                if (success) {
                    console.log(`✅ Popup ${popupId} permanently dismissed`);
                }
            } else {
                void popupService.dismissPopup(popupId, tenantId, 'manual');
            }

            if (remainingQueue.length > 0) {
                const [nextPopup, ...nextQueue] = remainingQueue;
                setCurrentPopup(nextPopup);
                setPopupQueue(nextQueue);
            } else {
                setCurrentPopup(null);
                setPopupQueue([]);
            }

        } catch (err) {
            console.error('Failed to dismiss popup:', err);

            const remainingQueue = popupQueue.filter(p => p._id !== popupId);
            if (remainingQueue.length > 0) {
                const [nextPopup, ...nextQueue] = remainingQueue;
                setCurrentPopup(nextPopup);
                setPopupQueue(nextQueue);
            } else {
                setCurrentPopup(null);
                setPopupQueue([]);
            }
        }
    }, [tenantId, popupQueue]);

    // Force show popup (for WebSocket integration)
    const forceShowPopup = useCallback((popup: PopupData) => {
        if (!shouldShowPopups) {

            return;
        }



        // Clear any dismissed status for this popup
        if (tenantId) {
            const key = `dismissed-popups-${tenantId}`;
            try {
                const dismissed = JSON.parse(localStorage.getItem(key) || '[]');
                const filtered = dismissed.filter((id: string) => id !== popup._id);
                localStorage.setItem(key, JSON.stringify(filtered));
            } catch (error) {
                console.warn('Failed to clear dismissed status:', error);
            }
        }

        // Add to front of queue with highest priority
        setPopupQueue(prev => {
            const filtered = prev.filter(p => p._id !== popup._id);
            return [popup, ...filtered];
        });

        // If no popup is currently showing, show immediately
        if (!currentPopup) {
            setCurrentPopup(popup);
            setPopupQueue(prev => prev.filter(p => p._id !== popup._id));
        }
    }, [tenantId, currentPopup, shouldShowPopups]);

    // Clear all popups
    const clearAllPopups = useCallback(() => {

        setCurrentPopup(null);
        setPopupQueue([]);
    }, []);

    // Refresh popups (manual only, no periodic polling)
    const refreshPopups = useCallback(async () => {

        await fetchInitialPopups(); // Use current state for refresh
    }, [fetchInitialPopups]);

    // Auto-show next popup when current one is dismissed
    useEffect(() => {
        if (!currentPopup && popupQueue.length > 0 && shouldShowPopups) {
            const timer = setTimeout(showNextPopup, 500);
            return () => clearTimeout(timer);
        }
    }, [currentPopup, showNextPopup, popupQueue.length, shouldShowPopups]);

    // 🚀 Initial system status check (always run, regardless of shouldShowPopups)
    useEffect(() => {


        // Clear any existing timeout
        if (initTimeoutRef.current) {
            clearTimeout(initTimeoutRef.current);
        }

        // Always run initialization if we have enabled flag and tenantId
        if (!enabled || !tenantId) {

            return;
        }

        if (!isPopupEligibleRoute()) {
            setPopupSystemEnabled(false);
            hasInitialized.current = false;
            setCurrentPopup(null);
            setPopupQueue([]);
            return;
        }

        // Debounced initialization to prevent rapid re-initialization
        initTimeoutRef.current = setTimeout(async () => {
            // Singleton check - only initialize once per session
            if (hasInitialized.current) {

                return;
            }

            hasInitialized.current = true;



            // STEP 1: Check popup system status first (this will set popupSystemEnabled)
            const systemEnabled = await checkPopupSystemStatus();

            // STEP 2: Only fetch popups if system is enabled AND we're on homepage
            if (systemEnabled && isPopupEligibleRoute()) {
                // Small delay to let state update, then fetch with forced enabled status
                setTimeout(() => {
                    fetchInitialPopups(true); // Force enabled since we know system is enabled
                }, 100);
            } else {

            }


        }, 500); // 500ms debounce

        return () => {
            if (initTimeoutRef.current) {
                clearTimeout(initTimeoutRef.current);
                initTimeoutRef.current = null;
            }
        };
    }, [enabled, tenantId, fetchInitialPopups, checkPopupSystemStatus, isPopupEligibleRoute]);

    // 🧹 Cleanup when shouldShowPopups becomes false
    useEffect(() => {
        if (!shouldShowPopups) {
            // Clear current popup and queue if we shouldn't show popups
            setCurrentPopup(null);
            setPopupQueue([]);
        }
    }, [shouldShowPopups]);

    // 🏠 Clear popups when leaving homepage
    useEffect(() => {
        if (!isPopupEligibleRoute()) {
            setCurrentPopup(null);
            setPopupQueue([]);
        }
    }, [isPopupEligibleRoute]);

    return {
        currentPopup: shouldShowPopups ? currentPopup : null,
        popupQueue: shouldShowPopups ? popupQueue : [],
        isLoading,
        error,
        dismissPopup,
        forceShowPopup,
        clearAllPopups,
        refreshPopups
    };
};
